/**
 * Telegram panel — displays events from 27 OSINT Telegram channels.
 *
 * Imports channel definitions from config, filters events where
 * source === 'telegram', groups by channel category tabs.
 */

import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import { TELEGRAM_CHANNELS } from '@/config/telegram-channels';
import type { TelegramCategory } from '@/config/telegram-channels';
import type { NormalizedEvent } from '@/sources/base-source';
import { relativeTime } from '@/utils/time';

const CATEGORY_TABS: { id: string; label: string; filter: TelegramCategory | 'all' }[] = [
  { id: 'all', label: 'All', filter: 'all' },
  { id: 'conflict', label: 'Conflict', filter: 'conflict' },
  { id: 'military', label: 'Military', filter: 'military' },
  { id: 'maritime', label: 'Maritime', filter: 'maritime' },
  { id: 'cyber', label: 'Cyber', filter: 'cyber' },
];

function getSeverityColor(severity: string | undefined): string {
  switch (severity) {
    case 'critical': return 'var(--severity-critical)';
    case 'high': return 'var(--severity-high)';
    case 'medium': return 'var(--severity-medium)';
    case 'low': return 'var(--severity-low)';
    default: return 'var(--text-muted)';
  }
}

export function createTelegramPanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'telegram-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%;';

  const cleanupFns: (() => void)[] = [];
  let activeCategory: TelegramCategory | 'all' = 'all';
  let renderPending = false;

  // Build channel lookup by handle/id
  const channelBySource = new Map<string, (typeof TELEGRAM_CHANNELS)[0]>();
  for (const ch of TELEGRAM_CHANNELS) {
    channelBySource.set(ch.handle, ch);
    channelBySource.set(ch.id, ch);
  }

  // --- Tabs ---
  const tabsContainer = document.createElement('div');
  tabsContainer.style.cssText = `
    display: flex; gap: 2px; padding: 8px 12px; border-bottom: 1px solid var(--border-primary);
    flex-shrink: 0; overflow-x: auto;
  `;

  function renderTabs(): void {
    tabsContainer.textContent = '';
    for (const tab of CATEGORY_TABS) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.style.cssText = `
        font-size: 10px; padding: 3px 10px; border-radius: 10px; cursor: pointer;
        border: 1px solid var(--border-primary); white-space: nowrap;
        background: ${tab.filter === activeCategory ? 'var(--accent)' : 'transparent'};
        color: ${tab.filter === activeCategory ? 'var(--bg-primary)' : 'var(--text-secondary)'};
      `;
      btn.textContent = tab.label;
      btn.onclick = () => {
        activeCategory = tab.filter;
        renderTabs();
        renderItems();
      };
      tabsContainer.appendChild(btn);
    }
  }

  container.appendChild(tabsContainer);

  // --- Items ---
  const itemsContainer = document.createElement('div');
  itemsContainer.style.cssText = 'flex: 1; overflow-y: auto; padding: 8px 12px;';
  container.appendChild(itemsContainer);

  function scheduleRender(): void {
    if (renderPending) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      renderItems();
    });
  }

  function renderItems(): void {
    const events = appStore.getState().events;
    const telegramEvents = events.filter(e => e.source === 'telegram');

    // Filter by category
    let filtered: NormalizedEvent[];
    if (activeCategory === 'all') {
      filtered = telegramEvents;
    } else {
      const channelsInCategory = new Set(
        TELEGRAM_CHANNELS
          .filter(ch => ch.category === activeCategory)
          .flatMap(ch => [ch.handle, ch.id]),
      );
      filtered = telegramEvents.filter(e => {
        // Check tags or description for channel identification
        if (e.tags) {
          for (const tag of e.tags) {
            if (channelsInCategory.has(tag)) return true;
          }
        }
        return false;
      });
    }

    // Sort newest first
    filtered.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    itemsContainer.textContent = '';

    if (filtered.length === 0) {
      const msg = document.createElement('div');
      msg.style.cssText = 'padding: 24px 8px; text-align: center; color: var(--text-muted); font-size: 12px;';
      msg.textContent = 'No Telegram messages yet';
      itemsContainer.appendChild(msg);

      // Show channel list instead
      const channelHeader = document.createElement('div');
      channelHeader.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-top: 12px; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.05em;';
      channelHeader.textContent = `Monitored Channels (${TELEGRAM_CHANNELS.length})`;
      itemsContainer.appendChild(channelHeader);

      const relevantChannels = activeCategory === 'all'
        ? TELEGRAM_CHANNELS
        : TELEGRAM_CHANNELS.filter(ch => ch.category === activeCategory);

      for (const ch of relevantChannels) {
        const chRow = document.createElement('div');
        chRow.style.cssText = `
          padding: 4px 8px; margin-bottom: 3px; border-radius: 4px;
          background: var(--bg-secondary); font-size: 11px;
        `;

        const chName = document.createElement('span');
        chName.style.cssText = 'color: var(--text-primary); font-weight: 500;';
        chName.textContent = ch.name;
        chRow.appendChild(chName);

        const chHandle = document.createElement('span');
        chHandle.style.cssText = 'color: var(--text-muted); margin-left: 6px;';
        chHandle.textContent = `@${ch.handle}`;
        chRow.appendChild(chHandle);

        if (ch.region) {
          const chRegion = document.createElement('span');
          chRegion.style.cssText = 'color: var(--text-muted); margin-left: 6px; font-size: 10px;';
          chRegion.textContent = `\u00B7 ${ch.region}`;
          chRow.appendChild(chRegion);
        }

        itemsContainer.appendChild(chRow);
      }

      return;
    }

    for (const ev of filtered.slice(0, 100)) {
      const item = document.createElement('div');
      item.style.cssText = `
        padding: 6px 8px; margin-bottom: 4px; border-radius: 4px;
        background: var(--bg-secondary); cursor: pointer;
      `;
      item.onclick = () => eventBus.emit('event:selected', ev);

      // Channel name
      const channelName = document.createElement('div');
      channelName.style.cssText = 'font-size: 10px; color: var(--text-muted); margin-bottom: 2px;';

      // Try to find channel info from tags
      let chName = ev.source;
      if (ev.tags) {
        for (const tag of ev.tags) {
          const ch = channelBySource.get(tag);
          if (ch) { chName = ch.name; break; }
        }
      }
      channelName.textContent = chName;
      item.appendChild(channelName);

      // Text
      const text = document.createElement('div');
      text.style.cssText = 'font-size: 11px; color: var(--text-primary); line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;';
      text.textContent = ev.title;
      item.appendChild(text);

      // Meta row
      const meta = document.createElement('div');
      meta.style.cssText = 'display: flex; align-items: center; gap: 6px; margin-top: 3px;';

      const time = document.createElement('span');
      time.style.cssText = 'font-size: 10px; color: var(--text-muted);';
      time.textContent = relativeTime(ev.timestamp);
      meta.appendChild(time);

      if (ev.severity && ev.severity !== 'info') {
        const sevDot = document.createElement('span');
        sevDot.style.cssText = `width: 6px; height: 6px; border-radius: 50%; background: ${getSeverityColor(ev.severity)};`;
        meta.appendChild(sevDot);
      }

      item.appendChild(meta);
      itemsContainer.appendChild(item);
    }
  }

  // Initial render
  renderTabs();
  renderItems();

  // Subscribe to events
  cleanupFns.push(
    appStore.subscribe(
      (s) => s.events,
      () => scheduleRender(),
    ),
  );

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}
